import { Inject, Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { DA_SERVICE_TOKEN, ITokenService } from '@delon/auth';
import { SettingsService, _HttpClient } from '@delon/theme';
import { firstValueFrom } from 'rxjs';

const N3_ORIGIN = 'https://n3.qne.cloud';

type QueryParams = Record<string, string | number | boolean | null | undefined>;
type QuinnyLaunchRoute =
  | '/ai/copilot'
  | '/ai/report-designer'
  | '/ai/report-analyzer'
  | '/ai/financial-advisor';

@Injectable({
  providedIn: 'root'
})
export class QuinnyN3RedirectService {
  private readonly qcaQuinnyRoutes = new Set([
    '/ai/copilot',
    '/ai/report-designer',
    '/ai/report-analyzer',
    '/ai/financial-advisor'
  ]);

  constructor(
    private router: Router,
    private settings: SettingsService,
    private http: _HttpClient,
    @Inject(DA_SERVICE_TOKEN) private tokenService: ITokenService
  ) {}

  private casRegistrationRequest: Promise<boolean> | null = null;

  shouldRedirectToN3Sync(): boolean | null {
    const company = this.settings.user?.['company'];
    if (!company) return false;

    const casRegistrationState = this.getCasRegistrationStateFromCompany(company);
    if (casRegistrationState !== null) {
      return !casRegistrationState;
    }

    if (company['country'] && company['country'] !== 'PH') {
      return true;
    }

    return null;
  }

  async shouldRedirectToN3(): Promise<boolean> {
    const syncDecision = this.shouldRedirectToN3Sync();
    if (syncDecision !== null) return syncDecision;

    const company = this.settings.user?.['company'];
    if (!company) return false;
    return !(await this.isCasOrCbaRegistered(company));
  }

  isQcaQuinnyRoute(route: string | null | undefined): boolean {
    if (!route) return false;
    return this.qcaQuinnyRoutes.has(this.normalizePath(route));
  }

  async openOrNavigate(route: string, queryParams?: QueryParams): Promise<void> {
    const syncDecision = this.shouldRedirectToN3Sync();
    const shouldRedirect =
      syncDecision !== null ? syncDecision : await this.shouldRedirectToN3();

    if (shouldRedirect && this.openInNewTab(route, queryParams)) {
      return;
    }

    await this.router.navigateByUrl(this.normalizePath(route));
  }

  async tryOpenInNewTab(
    route: string,
    queryParams?: QueryParams
  ): Promise<boolean> {
    if (!this.isQcaQuinnyRoute(route) || !(await this.shouldRedirectToN3())) {
      return false;
    }

    return this.openInNewTab(route, queryParams);
  }

  async redirectCurrentTab(route: string): Promise<boolean> {
    if (!this.isQcaQuinnyRoute(route) || !(await this.shouldRedirectToN3())) {
      return false;
    }

    const url = this.buildN3Url(route);
    if (!url) return false;

    window.location.href = url;
    return true;
  }

  /**
   * Sends public registration (and campaign query params) to N3.
   * No-op on n3.qne.cloud / QI so the N3 signup page is not redirected away.
   */
  redirectToRegister(queryParams?: Params): boolean {
    if (this.shouldSkipRegisterRedirect()) {
      return false;
    }

    window.location.replace(
      this.buildPublicN3Url('/passport/register', this.toQueryParams(queryParams))
    );
    return true;
  }

  private openInNewTab(route: string, queryParams?: QueryParams): boolean {
    const url = this.buildN3Url(route, queryParams);
    if (!url) return false;

    window.open(url, '_blank', 'noopener');
    return true;
  }

  private shouldSkipRegisterRedirect(): boolean {
    const host = window.location.hostname.toLowerCase();
    return host === 'n3.qne.cloud' || this.isQiHost();
  }

  private isQiHost(): boolean {
    const { hostname, port } = window.location;
    return (
      hostname === 'eintegrator.qne.cloud' ||
      (hostname === 'localhost' && port === '7002')
    );
  }

  private toQueryParams(queryParams?: Params): QueryParams | undefined {
    const result: QueryParams = {};
    new URLSearchParams(window.location.search).forEach((value, key) => {
      result[key] = value;
    });
    if (queryParams) {
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        result[key] = Array.isArray(value) ? String(value[0] ?? '') : String(value);
      });
    }
    return Object.keys(result).length ? result : undefined;
  }

  private buildPublicN3Url(route: string, queryParams?: QueryParams): string {
    const { path, searchParams } = this.parseRoute(route);
    this.appendQueryParams(searchParams, queryParams);
    const query = searchParams.toString();
    return query
      ? `${N3_ORIGIN}/#${path}?${query}`
      : `${N3_ORIGIN}/#${path}`;
  }

  private buildN3Url(route: string, queryParams?: QueryParams): string | null {
    const token = this.tokenService.get();
    if (!token) return null;

    const { path, searchParams } = this.parseRoute(this.resolveN3Route(route));
    this.appendQueryParams(searchParams, queryParams);
    searchParams.set('_token', this.encodeToken(token));

    return `${N3_ORIGIN}/#${path}?${searchParams.toString()}`;
  }

  private parseRoute(route: string): {
    path: string;
    searchParams: URLSearchParams;
  } {
    const [pathPart, queryString = ''] = route.split('?');
    return {
      path: this.normalizePath(pathPart),
      searchParams: new URLSearchParams(queryString)
    };
  }

  private normalizePath(route: string): string {
    const [path] = route.split('?');
    const normalized = path.startsWith('/') ? path : `/${path}`;
    return normalized.replace(/\/+$/, '') || '/';
  }

  private resolveN3Route(route: string): string {
    const { path, searchParams } = this.parseRoute(route);
    const normalized = path as QuinnyLaunchRoute;

    if (normalized === '/ai/report-analyzer') {
      searchParams.set('openReportAnalyzer', 'true');
      return `/dashboard?${searchParams.toString()}`;
    }

    if (normalized === '/ai/financial-advisor') {
      searchParams.set('openFinancialAdvisor', 'true');
      return `/dashboard?${searchParams.toString()}`;
    }

    return route;
  }

  private appendQueryParams(
    searchParams: URLSearchParams,
    queryParams?: QueryParams
  ): void {
    if (!queryParams) return;

    Object.entries(queryParams).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      searchParams.set(key, String(value));
    });
  }

  private encodeToken(token: unknown): string {
    const json = JSON.stringify(token);
    const bytes = new TextEncoder().encode(json);
    let binary = '';
    bytes.forEach(byte => {
      binary += String.fromCharCode(byte);
    });
    return btoa(binary);
  }

  private async isCasOrCbaRegistered(
    company: Record<string, unknown>
  ): Promise<boolean> {
    const casRegistrationState = this.getCasRegistrationStateFromCompany(company);
    if (casRegistrationState !== null) return casRegistrationState;

    this.casRegistrationRequest ??= firstValueFrom(
      this.http.get<Record<string, unknown>>('api/companyprofile/birInfo?_silent=true')
    )
      .then(birInfo => {
        return this.toBoolean(birInfo?.['isBirCasRegistered']);
      })
      .catch(() => {
        return false;
      })
      .finally(() => {
        this.casRegistrationRequest = null;
      });

    return this.casRegistrationRequest;
  }

  private getCasRegistrationStateFromCompany(
    company: Record<string, unknown>
  ): boolean | null {
    if (!Object.prototype.hasOwnProperty.call(company, 'isBirCasRegistered')) {
      return null;
    }

    return this.toBoolean(company['isBirCasRegistered']);
  }

  private toBoolean(value: unknown): boolean {
    return value === true || value === 'true';
  }
}
